import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import linkIcon from '../../../../assets/icons/generalIcons/linkIcon.svg';
import greyArrow from '../../../../assets/icons/generalIcons/greyArrow.svg';
import { GET_LAST_EVENTS } from '../../../../helpers/graphRequests';
import LoadingLogo from '../../../../assets/lottieAnimations/loadingLogo.json';
import Lottie from 'lottie-react';
import { useTranslation } from 'react-i18next';

export const ActivityTable = () => {
  const { t } = useTranslation();
  const { data, loading } = useQuery(GET_LAST_EVENTS);

  const [currentPage, setCurrentPage] = useState(1);
  const activityList = useMemo(() => {
    const raw = data?.lastEvents || [];
    const registrationKeys = new Set(
      raw
        .filter((item) => item?.eventType?.includes('handleRegistration'))
        .map((item) => `${item.transaction}:${item.user?.id}`),
    );

    return raw.filter((item) => {
      if (!(Number(item?.user?.internalId) > 0)) {
        return false;
      }
      if (!item?.eventType?.includes('handleUpgrade x2')) {
        return true;
      }
      return !registrationKeys.has(`${item.transaction}:${item.user?.id}`);
    });
  }, [data?.lastEvents]);

  const itemsPerPage = 9;

  const totalPages = Math.ceil(activityList.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = activityList.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const formatTimeAgo = (timestamp) => {
    const now = Math.floor(Date.now() / 1000);
    const diff = now - timestamp;
    if (diff < 60) return t('dashboard.recentActivity.table.time.sec', { count: diff });
    if (diff < 3600) return t('dashboard.recentActivity.table.time.min', { count: Math.floor(diff / 60) });
    if (diff < 86400) return t('dashboard.recentActivity.table.time.hour', { count: Math.floor(diff / 3600) });
    return t('dashboard.recentActivity.table.time.day', { count: Math.floor(diff / 86400) });
  };

  const formatAmount = (value) => {
    if (!value) return '0';
    return (Number(value) / 1e18).toFixed(2);
  };

  const getProgramLabel = (eventType) => {
    if (eventType.includes('x2') || eventType.includes('matrix type: 2'))
      return t('dashboard.recentActivity.table.program.duo');
    if (eventType.includes('x4') || eventType.includes('matrix type: 4'))
      return t('dashboard.recentActivity.table.program.quattro');
  };

  const getEventText = (eventType) => {
    if (eventType?.includes('handleUserReward')) return t('dashboard.recentActivity.table.event.receives');
    if (eventType?.includes('handleUpgrade')) return t('dashboard.recentActivity.table.event.upgrade');
    if (eventType?.includes('handleRegistration')) return t('dashboard.recentActivity.table.event.register');
  };

  const isReceivesEvent = (eventType) => eventType?.includes('handleUserReward');
  const isUpgradeEvent = (eventType) => eventType?.includes('handleUpgrade');

  const getEventIcon = (eventType) => {
    if (eventType?.includes('handleUserReward')) return '/icons/recentActivity/recieved.svg';
    if (eventType?.includes('handleUpgrade')) return '/icons/recentActivity/upgrade.svg';
    if (eventType?.includes('handleRegistration')) return '/icons/recentActivity/register.svg';
  };

  const getEventShadow = (eventType) => {
    if (eventType?.includes('handleUserReward')) return 'drop-shadow-[0_0_20px_#AF7FFF80]';
    if (eventType?.includes('handleUpgrade')) return 'drop-shadow-[0_0_15px_#51F68980]';
    if (eventType?.includes('handleRegistration')) return 'drop-shadow-[0_0_15px_#51F68980]';
  };

  return (
    <div className="flex flex-col justify-between space-y-[2rem] p-[2rem] border-[0.1rem] border-solid border-white-50 bg-black-300 rounded-[2rem] h-full w-full min-h-[64rem] lg:space-y-[1.2rem] lg:bg-white-30 lg:bg-blend-difference lg:min-h-[36rem]">
      {loading ? (
        <div className="flex items-center justify-center h-full w-full">
          <Lottie className="z-[3] h-[12rem] w-[12rem]" animationData={LoadingLogo} loop={true} />
        </div>
      ) : (
        <>
          <div className="flex flex-col space-y-[2rem] items-center lg:space-y-[1.2rem]">
            {currentItems.map((item, itemIndex) => {
              const eventIcon = getEventIcon(item.eventType);
              return (
                <div className="flex justify-between items-center w-full" key={itemIndex}>
                  <div className="flex items-center space-x-[2rem]">
                    <div className="flex items-center justify-center w-[4rem] h-[4rem] rounded-full bg-white-50">
                      {eventIcon ? (
                        <img
                          src={eventIcon}
                          className={`w-[2rem] h-[2rem] ${getEventShadow(item.eventType)}`}
                        />
                      ) : null}
                    </div>
                    <div className="flex items-center space-x-[2rem] lg:flex-col lg:space-x-0 lg:space-y-[0.5rem] lg:items-start">
                      <div className="flex items-center justify-start min-w-[7.5rem] lg:min-w-full">
                        <div className="flex items-center justify-center rounded-full bg-[rgba(175,127,255,0.10)] h-[3rem] px-[1rem] lg:bg-transparent lg:h-full lg:px-0">
                          <span className="text-[1.4rem] text-[#985AFF] font-400 tracking-[-0.42px] leading-none">
                            ID {item.user?.internalId}
                          </span>
                        </div>
                      </div>
                      <span className="text-[1.4rem] font-400 text-white leading-[1.96rem] lg:text-[1rem] lg:leading-[1.4rem]">
                        <span className="text-white-500">
                          {getEventText(item.eventType)}
                          {isUpgradeEvent(item.eventType) && getProgramLabel(item.eventType)
                            ? ` ${getProgramLabel(item.eventType)}`
                            : ''}
                        </span>{' '}
                        {isReceivesEvent(item.eventType) ? `${formatAmount(item.value)} POL` : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-[1rem]">
                    <span className="text-[1.4rem] font-400 leading-[1.96rem] text-white-500">
                      {formatTimeAgo(item.timestamp)}
                    </span>
                    {item.transaction && (
                      <a
                        href={`https://polygonscan.com/tx/${item.transaction}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:opacity-70"
                      >
                        <img src={linkIcon} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between w-full pt-[2rem]">
            <button
              onClick={handlePrevPage}
              className="flex items-center justify-center bg-white-50 w-[4rem] h-[4rem] rounded-full custom-transition hover:bg-white-100"
            >
              <img src={greyArrow} />
            </button>
            <div className="flex items-center space-x-[1rem] text-[1.4rem] leading-[1.96rem] font-400 custom-transition">
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageClick(page)}
                  className={currentPage === page ? 'text-white-500 custom-transition' : 'text-white custom-transition'}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={handleNextPage}
              className="flex items-center justify-center bg-white-50 w-[4rem] h-[4rem] rounded-full custom-transition hover:bg-white-100"
            >
              <img className="rotate-180" src={greyArrow} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
